// Debug configuration and utilities
export interface DebugConfig {
    /** 认证相关调试 */
    auth?: boolean;
    /** 国际化调试 */
    i18n?: boolean;
    /** 性能监控调试 */
    performance?: boolean;
    /** 路由相关调试 */
    routing?: boolean;
    /** 存储相关调试 */
    storage?: boolean;
    /** Loader 相关调试 */
    loader?: boolean;
    /** Action 相关调试 */
    action?: boolean;
    /** 中间件相关调试 */
    middleware?: boolean;

    /** 全局调试开关 */
    enabled?: boolean;
}

export class DebugLogger {
    private static instance: DebugLogger;
    private config: Required<DebugConfig>;

    private constructor(config: DebugConfig = {}) {
        this.config = {
            enabled: config.enabled ?? false, // 默认关闭调试，用户需要显式启用
            auth: config.auth ?? false,
            i18n: config.i18n ?? false,
            performance: config.performance ?? false, // 性能日志也默认关闭
            routing: config.routing ?? false,
            storage: config.storage ?? false,
            loader: config.loader ?? false,
            action: config.action ?? false,
            middleware: config.middleware ?? false,

        };
    }

    static getInstance(config?: DebugConfig): DebugLogger {
        if (!DebugLogger.instance) {
            DebugLogger.instance = new DebugLogger(config);
        }
        return DebugLogger.instance;
    }

    static updateConfig(config: Partial<DebugConfig>): void {
        const instance = DebugLogger.getInstance();
        instance.config = { ...instance.config, ...config };
    }

    private shouldLog(module: keyof Omit<DebugConfig, 'enabled'>): boolean {
        return this.config.enabled && this.config[module];
    }

    // 认证相关调试
    auth(message: string, data?: unknown): void {
        if (process.env.NODE_ENV === 'development' && this.shouldLog('auth')) {
            console.log(`🔐 Auth: ${message}`, data ? data : '');
        }
    }

    // 国际化调试
    i18n(message: string, data?: unknown): void {
        if (process.env.NODE_ENV === 'development' && this.shouldLog('i18n')) {
            console.log(`🌍 I18n: ${message}`, data ? data : '');
        }
    }

    // 性能监控调试
    performance(message: string, data?: unknown): void {
        if (process.env.NODE_ENV === 'development' && this.shouldLog('performance')) {
            console.log(`⚡ Performance: ${message}`, data ? data : '');
        }
    }

    // 路由相关调试
    routing(message: string, data?: unknown): void {
        if (process.env.NODE_ENV === 'development' && this.shouldLog('routing')) {
            console.log(`🚀 Routing: ${message}`, data ? data : '');
        }
    }

    // 存储相关调试
    storage(message: string, data?: unknown): void {
        if (process.env.NODE_ENV === 'development' && this.shouldLog('storage')) {
            console.log(`💾 Storage: ${message}`, data ? data : '');
        }
    }

    // 错误日志（总是显示）
    error(message: string, error?: unknown): void {
        if (process.env.NODE_ENV === 'development' && this.config.enabled) {
            console.error(`❌ Error: ${message}`, error ? error : '');
        }
    }

    // 警告日志（总是显示）
    warn(message: string, data?: unknown): void {
        if (process.env.NODE_ENV === 'development' && this.config.enabled) {
            console.warn(`⚠️ Warning: ${message}`, data ? data : '');
        }
    }

    // 成功日志
    success(message: string, data?: unknown): void {
        if (process.env.NODE_ENV === 'development' && this.config.enabled) {
            console.log(`✅ Success: ${message}`, data ? data : '');
        }
    }

    // 通用日志（不属于特定模块）
    log(message: string, data?: unknown): void {
        if (process.env.NODE_ENV === 'development' && this.config.enabled) {
            console.log(`📝 Log: ${message}`, data ? data : '');
        }
    }

    // 信息日志
    info(message: string, data?: unknown): void {
        if (process.env.NODE_ENV === 'development' && this.config.enabled) {
            console.info(`ℹ️ Info: ${message}`, data ? data : '');
        }
    }

    // Loader 相关调试
    loader(message: string, data?: unknown): void {
        if (process.env.NODE_ENV === 'development' && this.shouldLog('loader')) {
            console.log(`🔄 Loader: ${message}`, data ? data : '');
        }
    }

    // Action 相关调试
    action(message: string, data?: unknown): void {
        if (process.env.NODE_ENV === 'development' && this.shouldLog('action')) {
            console.log(`⚡ Action: ${message}`, data ? data : '');
        }
    }

    // 中间件相关调试
    middleware(message: string, data?: unknown): void {
        if (process.env.NODE_ENV === 'development' && this.shouldLog('middleware')) {
            console.log(`🔧 Middleware: ${message}`, data ? data : '');
        }
    }

    // 获取当前配置
    getConfig(): Required<DebugConfig> {
        return { ...this.config };
    }
}

// 导出便捷的调试器实例
export const debug = DebugLogger.getInstance();

// 导出配置更新函数
export const updateDebugConfig = DebugLogger.updateConfig;